import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";

const API_BASE = import.meta.env.VITE_API_URL || "/api";
const PLANS = ["free", "starter", "pro", "business", "enterprise"] as const;

async function fetchOrganization(id: string) {
  const res = await fetch(`${API_BASE}/admin/organizations/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch organization");
  return res.json();
}

async function fetchReports(id: string, page: number) {
  const res = await fetch(`${API_BASE}/admin/organizations/${id}/reports?page=${page}&limit=10`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch reports");
  return res.json();
}

export function AdminOrganizationDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"overview" | "members" | "domains" | "reports">("overview");
  const [reportPage, setReportPage] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-organization", id],
    queryFn: () => fetchOrganization(id),
  });

  const { data: reportsData } = useQuery({
    queryKey: ["admin-organization-reports", id, reportPage],
    queryFn: () => fetchReports(id, reportPage),
    enabled: activeTab === "reports",
  });

  const setPlanMutation = useMutation({
    mutationFn: async (plan: string) => {
      const res = await fetch(`${API_BASE}/admin/organizations/${id}/set-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan }),
      });
      if (!res.ok) throw new Error("Failed to update plan");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
      setSelectedPlan("");
    },
  });

  const resetUsageMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/admin/organizations/${id}/reset-usage`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to reset usage");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
    },
  });

  const verifyDomainMutation = useMutation({
    mutationFn: async (domainId: string) => {
      const res = await fetch(`${API_BASE}/admin/domains/${domainId}/verify`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to verify domain");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: async (domainId: string) => {
      const res = await fetch(`${API_BASE}/admin/domains/${domainId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete domain");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to remove member");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organization", id] });
    },
  });

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  }

  if (error || !data) {
    return <div className="p-8 text-center text-red-600">Failed to load organization</div>;
  }

  const { organization: org, members, domains, stats } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-indigo-200 mb-2">
            <Link to="/admin" className="hover:text-white">Admin</Link>
            <span>/</span>
            <Link to="/admin/organizations" className="hover:text-white">Organizations</Link>
            <span>/</span>
            <span>{org.name}</span>
          </div>
          <h1 className="text-3xl font-bold">{org.name}</h1>
          <p className="mt-1 text-indigo-100">{org.slug}</p>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-primary">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Plan</div>
              <div className="stat-value">{org.plan.charAt(0).toUpperCase() + org.plan.slice(1)}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-teal">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Domains</div>
              <div className="stat-value">{domains.length} / {org.domainLimit}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-amber">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Reports</div>
              <div className="stat-value">{stats.totalReports.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-emerald">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Messages</div>
              <div className="stat-value">{stats.totalMessages.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-purple">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Last Report</div>
              <div className="stat-value text-base">
                {stats.lastReportAt ? new Date(stats.lastReportAt).toLocaleDateString() : "Never"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {(["overview", "members", "domains", "reports"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Details */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Organization Details</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">ID</dt>
                <dd className="font-mono text-sm">{org.id}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Slug</dt>
                <dd>{org.slug}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd>{new Date(org.createdAt).toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Stripe Customer</dt>
                <dd className="font-mono text-sm">{org.stripeCustomerId || "—"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Subscription</dt>
                <dd className="font-mono text-sm">{org.stripeSubscriptionId || "—"}</dd>
              </div>
            </dl>
          </div>

          {/* Usage vs Limits */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Usage vs Limits</h3>
            <div className="space-y-4">
              {/* Monthly Messages */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Monthly Messages</span>
                  <span className="font-medium">
                    {org.monthlyMessageCount.toLocaleString()} / {org.messageLimit.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      org.monthlyMessageCount / org.messageLimit > 0.9
                        ? "bg-red-500"
                        : org.monthlyMessageCount / org.messageLimit > 0.7
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                    }`}
                    style={{ width: `${Math.min(100, (org.monthlyMessageCount / org.messageLimit) * 100)}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {Math.round((org.monthlyMessageCount / org.messageLimit) * 100)}% used
                </div>
              </div>

              {/* Domains */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Domains</span>
                  <span className="font-medium">
                    {domains.length} / {org.domainLimit}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      domains.length / org.domainLimit > 0.9
                        ? "bg-red-500"
                        : domains.length / org.domainLimit > 0.7
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                    }`}
                    style={{ width: `${Math.min(100, (domains.length / org.domainLimit) * 100)}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {Math.round((domains.length / org.domainLimit) * 100)}% used
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Pass Rate</span>
                  <span className={stats.passRate >= 90 ? "text-emerald-600 font-medium" : "text-amber-600 font-medium"}>
                    {stats.passRate}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Messages (All Time)</span>
                  <span>{stats.totalMessages.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Usage Resets</span>
                  <span>{new Date(org.usageResetAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => resetUsageMutation.mutate()}
              disabled={resetUsageMutation.isPending}
              className="btn btn-secondary btn-sm mt-4"
            >
              {resetUsageMutation.isPending ? "Resetting..." : "Reset Usage Counter"}
            </button>
          </div>

          {/* Plan Management */}
          <div className="card lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">Change Plan</h3>
            <div className="flex gap-3">
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="input input-primary flex-1 max-w-xs"
              >
                <option value="">Select new plan...</option>
                {PLANS.filter((p) => p !== org.plan).map((plan) => (
                  <option key={plan} value={plan}>
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </option>
                ))}
              </select>
              <button
                onClick={() => selectedPlan && setPlanMutation.mutate(selectedPlan)}
                disabled={!selectedPlan || setPlanMutation.isPending}
                className="btn btn-primary"
              >
                {setPlanMutation.isPending ? "Updating..." : "Update Plan"}
              </button>
            </div>
            {setPlanMutation.isSuccess && (
              <p className="mt-2 text-sm text-emerald-600">Plan updated successfully!</p>
            )}
          </div>
        </div>
      )}

      {activeTab === "members" && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Members ({members.length})</h3>
          <table className="table">
            <thead>
              <tr className="table-header">
                <th className="table-header-cell">User</th>
                <th className="table-header-cell">Role</th>
                <th className="table-header-cell">Verified</th>
                <th className="table-header-cell">Joined</th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member: any) => (
                <tr key={member.id} className="table-row">
                  <td className="table-cell">
                    <Link
                      to={`/admin/users/${member.user.id}` as any}
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      {member.user.name}
                    </Link>
                    <div className="text-sm text-muted-foreground">{member.user.email}</div>
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${member.role === "owner" ? "badge-primary" : "badge-secondary"}`}>
                      {member.role}
                    </span>
                  </td>
                  <td className="table-cell">
                    {member.user.emailVerified ? (
                      <span className="text-emerald-600">Yes</span>
                    ) : (
                      <span className="text-amber-600">No</span>
                    )}
                  </td>
                  <td className="table-cell text-sm text-muted-foreground">
                    {new Date(member.createdAt).toLocaleDateString()}
                  </td>
                  <td className="table-cell text-right">
                    <button
                      onClick={() => {
                        if (confirm("Remove this member from the organization?")) {
                          removeMemberMutation.mutate(member.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-500 text-sm"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "domains" && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Domains ({domains.length} / {org.domainLimit})</h3>
          {domains.length > 0 ? (
            <table className="table">
              <thead>
                <tr className="table-header">
                  <th className="table-header-cell">Domain</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell text-right">Reports</th>
                  <th className="table-header-cell text-right">Messages</th>
                  <th className="table-header-cell">Last Report</th>
                  <th className="table-header-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain: any) => (
                  <tr key={domain.id} className="table-row">
                    <td className="table-cell">
                      <div className="font-medium">{domain.domain}</div>
                      <div className="text-xs text-muted-foreground">
                        Added {new Date(domain.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="table-cell">
                      {domain.verified ? (
                        <span className="badge bg-emerald-100 text-emerald-800">Verified</span>
                      ) : (
                        <span className="badge bg-amber-100 text-amber-800">Pending</span>
                      )}
                    </td>
                    <td className="table-cell text-right font-medium">
                      {domain.reportCount?.toLocaleString() || 0}
                    </td>
                    <td className="table-cell text-right">
                      {domain.messageCount?.toLocaleString() || 0}
                    </td>
                    <td className="table-cell text-sm text-muted-foreground">
                      {domain.lastReportAt
                        ? new Date(domain.lastReportAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="table-cell text-right space-x-3">
                      {!domain.verified && (
                        <button
                          onClick={() => verifyDomainMutation.mutate(domain.id)}
                          className="text-emerald-600 hover:text-emerald-500 text-sm"
                        >
                          Verify
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (confirm("Delete this domain? This will also delete all associated reports.")) {
                            deleteDomainMutation.mutate(domain.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-500 text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted-foreground">No domains added yet.</p>
          )}
        </div>
      )}

      {activeTab === "reports" && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Reports</h3>
          {reportsData?.reports && reportsData.reports.length > 0 ? (
            <>
              <table className="table">
                <thead>
                  <tr className="table-header">
                    <th className="table-header-cell">Report</th>
                    <th className="table-header-cell">Domain</th>
                    <th className="table-header-cell">Date Range</th>
                    <th className="table-header-cell text-right">Messages</th>
                    <th className="table-header-cell text-right">Pass Rate</th>
                    <th className="table-header-cell">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {reportsData.reports.map((report: any) => (
                    <tr key={report.id} className="table-row">
                      <td className="table-cell">
                        <div className="font-medium">{report.orgName}</div>
                        <div className="text-xs text-muted-foreground font-mono">{report.reportId}</div>
                      </td>
                      <td className="table-cell">{report.policyDomain}</td>
                      <td className="table-cell text-sm">
                        {new Date(report.dateRangeBegin).toLocaleDateString()} - {new Date(report.dateRangeEnd).toLocaleDateString()}
                      </td>
                      <td className="table-cell text-right">{report.totalCount.toLocaleString()}</td>
                      <td className="table-cell text-right">
                        <span className={report.passCount / report.totalCount >= 0.9 ? "text-emerald-600" : "text-amber-600"}>
                          {Math.round((report.passCount / report.totalCount) * 100)}%
                        </span>
                      </td>
                      <td className="table-cell text-sm text-muted-foreground">
                        {new Date(report.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {reportsData.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between border-t px-4 py-3 mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {reportsData.pagination.page} of {reportsData.pagination.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setReportPage((p) => Math.max(1, p - 1))}
                      disabled={reportPage === 1}
                      className="btn btn-secondary btn-sm"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setReportPage((p) => Math.min(reportsData.pagination.totalPages, p + 1))}
                      disabled={reportPage === reportsData.pagination.totalPages}
                      className="btn btn-secondary btn-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">No reports received yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
